<template>
  <div class="flex w-full h-full max-w-7xl mx-auto space-x-6 p-4 pb-6 font-sans">
    <!-- 左侧：搜索历史 -->
    <div class="w-64 flex-shrink-0 bg-archPanel border border-gray-200 rounded-xl p-6 h-full flex flex-col shadow-sm">
      <h3 class="text-archGold font-bold mb-6 flex items-center space-x-2"><span>🕒</span> <span>搜索历史</span></h3>
      <div class="flex-grow space-y-2 overflow-y-auto custom-scrollbar pr-2">
        <div v-if="searchHistory.length === 0" class="text-xs text-gray-400 text-center mt-4">暂无历史记录</div>
        <div v-for="(item, idx) in searchHistory" :key="idx" @click="useHistory(item)" class="group flex justify-between items-center text-sm p-3 rounded-lg bg-gray-50 hover:bg-white border border-transparent hover:border-archGold/50 text-gray-700 cursor-pointer transition mb-2 shadow-sm font-medium">
          <span class="truncate w-full">{{ item }}</span>
        </div>
      </div>
      <button v-if="searchHistory.length > 0" @click="clearHistory" class="w-full py-2 text-gray-500 font-bold text-sm hover:text-red-500 bg-gray-50 hover:bg-red-50 transition mt-4 border border-gray-200 rounded-lg">清空历史记录</button>
    </div>

    <!-- 右侧：主体检索区 -->
    <div class="flex-grow flex flex-col h-full overflow-hidden">
      <!-- 搜索输入面板 -->
      <div class="bg-archPanel border border-gray-200 rounded-xl p-6 mb-6 shadow-sm flex-shrink-0">
        <div class="flex border-b border-gray-200 mb-6">
          <button v-for="tab in ['文字检索', '草图检索', '图片检索']" :key="tab" @click="activeTab = tab" class="px-8 py-3 font-bold text-sm transition-colors relative" :class="activeTab === tab ? 'text-archGold' : 'text-gray-500 hover:text-gray-800'">
            {{ tab }}
            <div v-if="activeTab === tab" class="absolute bottom-[-1px] left-0 w-full h-[2px] bg-archGold"></div>
          </button>
        </div>
        
        <!-- 文字检索 -->
        <div v-if="activeTab === '文字检索'" class="flex flex-col space-y-4">
          <div class="flex space-x-4">
            <input v-model="searchText" @keyup.enter="startSearch" type="text" placeholder="输入建筑描述..." class="flex-grow bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-800 font-medium focus:outline-none focus:border-archGold transition shadow-sm" :disabled="isSearching" />
            <button @click="startSearch" :disabled="isSearching || !searchText.trim()" class="bg-gradient-to-r from-archGold to-[#8C6D23] text-white font-bold px-10 py-3 rounded-lg hover:shadow-lg transition-all duration-300 disabled:opacity-50">检索</button>
          </div>
          <div class="flex items-center space-x-3 text-xs text-gray-500 font-medium">
            <span>热门推荐：</span>
            <span @click="useHistory('现代主义风格别墅')" class="hover:text-archGold cursor-pointer font-bold">现代主义风格别墅</span>
            <span @click="useHistory('绿色生态建筑')" class="hover:text-archGold cursor-pointer font-bold">绿色生态建筑</span>
          </div>
        </div>
        
        <!-- 图片/草图检索 -->
        <div v-else class="flex flex-col items-center w-full">
          <input type="file" ref="fileInput" @change="handleFileUpload" class="hidden" accept="image/*" />
          <div v-if="!uploadedImageUrl" @click="fileInput?.click()" class="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-500 bg-gray-50 hover:bg-archGold/5 hover:border-archGold hover:text-archGold transition-all duration-300 cursor-pointer">
            <span class="text-2xl mb-2">⬆️</span><p class="font-bold">点击上传或拖拽图片</p><p class="text-xs mt-1 font-medium">支持 JPG, PNG</p>
            <!-- 若从历史记录点过来，显示提示 -->
            <p v-if="selectedFileName" class="text-archGold text-sm mt-2 font-bold">上次使用：{{ selectedFileName }}</p>
          </div>
          <div v-else class="w-full relative flex flex-col items-center">
            <div class="w-full h-32 bg-gray-100 rounded-lg border border-gray-300 flex items-center justify-center relative overflow-hidden group hover:border-archGold transition-all">
               <img :src="uploadedImageUrl" class="h-full object-contain" />
               <div @click="fileInput?.click()" class="absolute inset-0 bg-white/70 flex items-center justify-center opacity-0 group-hover:opacity-100 transition cursor-pointer">
                 <span class="text-gray-800 font-bold text-sm">点击重新上传</span>
               </div>
            </div>
            <button @click="startSearch" :disabled="isSearching" class="mt-4 bg-gradient-to-r from-archGold to-[#8C6D23] text-white font-bold px-12 py-2 rounded-lg hover:shadow-lg transition-all duration-300 disabled:opacity-50">开始检索该图片</button>
          </div>
        </div>
      </div>

      <!-- 进度条 -->
      <div v-if="isSearching" class="bg-archPanel border border-gray-200 rounded-xl p-8 mb-6 flex flex-col items-center justify-center space-y-4 shadow-sm">
        <div class="text-archGold flex items-center space-x-3 w-full max-w-md font-bold">
          <svg class="animate-spin h-5 w-5" xmlns="[http://www.w3.org/2000/svg](http://www.w3.org/2000/svg)" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
          <span class="flex-grow">{{ searchStatusText }}</span><span class="font-mono text-lg">{{ searchProgress }}%</span>
        </div>
        <div class="w-full max-w-md bg-gray-200 rounded-full h-2 overflow-hidden"><div class="bg-gradient-to-r from-[#D4AF37] to-[#F3E5AB] h-2 rounded-full transition-all duration-300" :style="`width: ${searchProgress}%`"></div></div>
      </div>

      <!-- 结果 -->
      <div v-if="showResults" class="flex-grow overflow-y-auto custom-scrollbar pb-8">
        <h3 class="text-lg font-bold text-gray-900 mb-6">为您匹配到 <span class="text-archGold">3</span> 个高相关性案例</h3>
        <div class="grid grid-cols-3 gap-6">
          <div class="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-archGold transition group shadow-sm hover:shadow-md">
            <div class="h-40 bg-gray-100 relative flex items-center justify-center text-gray-400 font-medium"><span class="text-sm">案例图片</span><div class="absolute top-2 left-2 bg-white/90 border border-archGold/50 text-archGold text-xs px-2 py-1 rounded font-bold shadow-sm">96% 匹配</div></div>
            <div class="p-4"><h4 class="text-gray-900 font-title-heavy mb-1 truncate">流水别墅</h4><p class="text-gray-500 text-xs mb-3 font-medium">Frank Lloyd Wright</p></div>
          </div>
          <div class="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-archGold transition group shadow-sm hover:shadow-md">
            <div class="h-40 bg-gray-100 relative flex items-center justify-center text-gray-400 font-medium"><span class="text-sm">案例图片</span><div class="absolute top-2 left-2 bg-white/90 border border-archGold/50 text-archGold text-xs px-2 py-1 rounded font-bold shadow-sm">88% 匹配</div></div>
            <div class="p-4"><h4 class="text-gray-900 font-title-heavy mb-1 truncate">萨伏伊别墅</h4><p class="text-gray-500 text-xs mb-3 font-medium">Le Corbusier</p></div>
          </div>
          <div class="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-archGold transition group shadow-sm hover:shadow-md">
            <div class="h-40 bg-gray-100 relative flex items-center justify-center text-gray-400 font-medium"><span class="text-sm">案例图片</span><div class="absolute top-2 left-2 bg-white/90 border border-archGold/50 text-archGold text-xs px-2 py-1 rounded font-bold shadow-sm">82% 匹配</div></div>
            <div class="p-4"><h4 class="text-gray-900 font-title-heavy mb-1 truncate">玻璃屋</h4><p class="text-gray-500 text-xs mb-3 font-medium">Philip Johnson</p></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { globalState } from '../store';

const activeTab = ref('文字检索');
const searchText = ref('');
const isSearching = ref(false);
const searchProgress = ref(0);
const searchStatusText = ref('');
const showResults = ref(false);

const searchHistory = ref<string[]>([]);
const fileInput = ref<HTMLInputElement | null>(null);
const selectedFileName = ref('');
const uploadedImageUrl = ref('');

const getStorageKey = () => {
  return globalState.currentUser ? `arch_search_history_${globalState.currentUser}` : 'arch_search_history_guest';
};

const loadHistory = () => {
  const saved = localStorage.getItem(getStorageKey());
  searchHistory.value = saved ? JSON.parse(saved) : [];
};

watch(() => globalState.currentUser, loadHistory);
onMounted(loadHistory);

const clearHistory = () => {
  searchHistory.value = [];
  localStorage.removeItem(getStorageKey());
};

const handleFileUpload = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    const file = target.files[0];
    selectedFileName.value = file.name;
    uploadedImageUrl.value = URL.createObjectURL(file);
    showResults.value = false;
  }
};

const useHistory = (text: string) => {
  if (text.startsWith('[图片检索] ') || text.startsWith('[草图检索] ')) {
     // 从记录中提取类型和文件名
     const match = text.match(/^\[(.*?)\] (.*)$/);
     if (match) {
        activeTab.value = match[1]; // 切换到对应的图片/草图 Tab
        selectedFileName.value = match[2]; // 显示文件名提示
        uploadedImageUrl.value = ''; // 无法还原本地 blob，需用户重新上传或直接用文件名检索
     }
  } else {
     activeTab.value = '文字检索';
     searchText.value = text;
  }
  // 不直接 startSearch，让用户确认后再搜，特别是图片需要重新上传
  showResults.value = false;
};

const startSearch = () => {
  let historyEntry = '';
  if (activeTab.value === '文字检索') {
     if (!searchText.value.trim()) return;
     historyEntry = searchText.value;
  } else {
     if (!selectedFileName.value) return;
     historyEntry = `[${activeTab.value}] ${selectedFileName.value}`;
  }
  
  if (historyEntry && !searchHistory.value.includes(historyEntry)) {
    searchHistory.value.unshift(historyEntry);
    localStorage.setItem(getStorageKey(), JSON.stringify(searchHistory.value));
  }

  isSearching.value = true;
  showResults.value = false;
  searchProgress.value = 15;
  searchStatusText.value = '正在提取输入特征...';

  setTimeout(() => { searchProgress.value = 40; searchStatusText.value = '连接 Milvus 向量数据库...'; }, 800);
  setTimeout(() => { searchProgress.value = 75; searchStatusText.value = '匹配 Neo4j 建筑知识图谱...'; }, 1600);
  setTimeout(() => {
    searchProgress.value = 100; searchStatusText.value = '检索完成，正在生成结果...';
    setTimeout(() => { isSearching.value = false; showResults.value = true; }, 500);
  }, 2400);
};
</script>